import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import "./CrmJourneyTimeline.css";

const normalizeItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const safeText = (value) => String(value || "").trim();
const getEventDate = (event) => event.created_at || event.completed_at || event.updated_at || event.when_at;

const formatDateTime = (value, t) => {
  if (!value) return t("crm_timeline_no_date", { defaultValue: "No date" });
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const getAttachments = (event) => Array.isArray(event?.file_attachments) ? event.file_attachments : [];

const compactDescription = (event) => {
  const raw = event.content || event.notes || event.description || event.message || event.details || "";
  return safeText(raw).replace(/\s+/g, " ");
};

const eventColor = {
  contact: "contact",
  lead: "lead",
  task: "task",
  photo: "photo",
  "task-done": "done",
  deal: "deal",
  won: "won",
  email: "contact",
  call: "contact",
  telegram: "contact",
  csv: "contact",
  system: "system",
};

const detectEvent = (event, t) => {
  const title = safeText(event.subject || event.title || event.name);
  const content = compactDescription(event);
  const rawType = safeText(event.kind || event.entity_type || event.type || event.channel || event.status).toLowerCase();
  const text = `${title} ${content} ${rawType}`.toLowerCase();

  if (getAttachments(event).length > 0 || text.includes("photo")) return { icon: "▧", kind: "photo", bucket: "task", label: t("crm_timeline_photos", { defaultValue: "Photos" }), title: title || t("crm_timeline_photos_added", { defaultValue: "Photos added" }) };
  if (text.includes("telegram")) return { icon: "✦", kind: "telegram", bucket: "contact", label: t("crm_timeline_telegram"), title: title || t("crm_timeline_telegram_message") };
  if (text.includes("csv") || text.includes("import")) return { icon: "⇣", kind: "csv", bucket: "contact", label: t("crm_timeline_csv"), title: title || t("crm_timeline_csv_import", { defaultValue: "CSV import" }) };
  if (text.includes("won") || text.includes("lost")) return { icon: "✓", kind: "won", bucket: "won", label: t("crm_timeline_won_lost"), title: title || t("crm_timeline_final_result") };
  if (text.includes("deal") || text.includes("stage")) return { icon: "▣", kind: "deal", bucket: "deal", label: t("crm_timeline_deal", { defaultValue: "Deal" }), title: title || t("crm_timeline_deal", { defaultValue: "Deal" }) };
  if (text.includes("lead") || rawType === "lead") return { icon: "◉", kind: "lead", bucket: "lead", label: t("crm_timeline_lead", { defaultValue: "Lead" }), title: title || t("crm_timeline_lead", { defaultValue: "Lead" }) };
  if (text.includes("completed") || text.includes("done") || text.includes("marked as done")) return { icon: "✓", kind: "task-done", bucket: "task", label: t("crm_timeline_done", { defaultValue: "Done" }), title: title || t("crm_timeline_done", { defaultValue: "Done" }) };
  if (text.includes("task") || text.includes("scheduled") || rawType === "task") return { icon: "□", kind: "task", bucket: "task", label: t("crm_timeline_task", { defaultValue: "Task" }), title: title.replace(/^task:\s*/i, "") || t("crm_timeline_task", { defaultValue: "Task" }) };
  if (event.channel === "email" || event.type === "email") return { icon: "@", kind: "email", bucket: "contact", label: t("crm_timeline_email"), title: title || t("crm_timeline_email") };
  if (event.channel === "call" || event.type === "call") return { icon: "☎", kind: "call", bucket: "contact", label: t("crm_timeline_call", { defaultValue: "Call" }), title: title || t("crm_timeline_call", { defaultValue: "Call" }) };
  if (text.includes("contact")) return { icon: "●", kind: "contact", bucket: "contact", label: t("crm_timeline_contact", { defaultValue: "Contact" }), title: title || t("crm_timeline_contact", { defaultValue: "Contact" }) };
  return { icon: "•", kind: "system", bucket: "all", label: t("crm_timeline_system", { defaultValue: "System" }), title: title || t("crm_timeline_event", { defaultValue: "Event" }) };
};

const buildTimelineUrl = (target, limit) => {
  if (!target) return "";
  if (target.url) return target.url;

  const type = String(target.type || "").trim();
  const value = target.uiId || target.ui_id || target.id;
  const allowed = new Set(["contact", "lead", "deal", "task"]);

  if (!allowed.has(type) || !value) return "";
  const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
  return `/activities/timeline/${encodeURIComponent(type)}/${encodeURIComponent(value)}${qs}`;
};

const filters = [
  { key: "all", label: "crm_timeline_filter_all", icon: "All" },
  { key: "contact", label: "crm_timeline_filter_contact", icon: "C" },
  { key: "lead", label: "crm_timeline_filter_lead", icon: "L" },
  { key: "task", label: "crm_timeline_filter_task", icon: "T" },
  { key: "deal", label: "crm_timeline_filter_deal", icon: "D" },
  { key: "won", label: "crm_timeline_filter_won_lost", icon: "W" },
];

export default function CrmJourneyTimeline({ target, title, scopeLabel, limit = 50, events: providedEvents }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(6);
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [openEventKey, setOpenEventKey] = useState(null);

  const timelineUrl = useMemo(() => buildTimelineUrl(target, limit), [target, limit]);

  useEffect(() => {
    let ignore = false;
    setActiveFilter("all");
    setVisibleCount(6);
    setOpenEventKey(null);

    if (providedEvents) {
      setEvents(normalizeItems(providedEvents));
      setLoading(false);
      setError("");
      return () => { ignore = true; };
    }

    if (!timelineUrl) {
      setEvents([]);
      setError("");
      return () => { ignore = true; };
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get(timelineUrl);
        if (!ignore) setEvents(normalizeItems(response.data));
      } catch (err) {
        if (!ignore) {
          setEvents([]);
          const msg = err?.response?.data?.detail || err?.message || t("crm_timeline_load_error", { defaultValue: "Timeline could not be loaded." });
          setError(typeof msg === "string" ? msg : t("crm_timeline_load_error", { defaultValue: "Timeline could not be loaded." }));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => { ignore = true; };
  }, [providedEvents, timelineUrl, t]);

  const preparedEvents = useMemo(() => {
    return [...events]
      .sort((a, b) => {
        const at = new Date(getEventDate(a) || 0).getTime();
        const bt = new Date(getEventDate(b) || 0).getTime();
        if (at !== bt) return bt - at;
        return Number(b.ui_id || b.id || 0) - Number(a.ui_id || a.id || 0);
      })
      .map((event) => ({ ...event, presentation: detectEvent(event, t) }));
  }, [events, t]);

  const activeKinds = useMemo(() => {
    return new Set(preparedEvents.map((event) => event.presentation.bucket));
  }, [preparedEvents]);

  useEffect(() => {
    // Do not keep the user stuck on an empty filter like Won/Lost when new task/lead/photo
    // events arrive under another bucket. In task details the safest default is always All.
    if (activeFilter !== "all" && preparedEvents.length > 0 && !activeKinds.has(activeFilter)) {
      setActiveFilter("all");
      setVisibleCount(6);
    }
  }, [activeFilter, activeKinds, preparedEvents.length]);

  const filteredEvents = useMemo(() => {
    if (activeFilter === "all") return preparedEvents;
    return preparedEvents.filter((event) => event.presentation.bucket === activeFilter);
  }, [activeFilter, preparedEvents]);

  const shownEvents = expanded ? filteredEvents : filteredEvents.slice(0, visibleCount);
  const hasMore = !expanded && filteredEvents.length > visibleCount;

  return (
    <section className={`crm-journey-timeline ${expanded ? "is-expanded" : ""}`}>
      <div className="crm-journey-timeline__head">
        <div className="crm-journey-timeline__title-block">
          <span className="crm-journey-timeline__eyebrow">{t("crm_timeline_customer_journey", { defaultValue: "Customer Journey" })}</span>
          <h3>{title || t("crm_timeline_title", { defaultValue: "Timeline" })}</h3>
        </div>
        <div className="crm-journey-timeline__actions">
          {scopeLabel && <span className="crm-journey-timeline__scope">{scopeLabel}</span>}
          <button
            type="button"
            className="crm-journey-timeline__expand"
            onClick={() => setExpanded((value) => !value)}
            title={expanded ? t("close", { defaultValue: "Close" }) : t("crm_timeline_expand", { defaultValue: "Open timeline fullscreen" })}
            aria-label={expanded ? t("close", { defaultValue: "Close" }) : t("crm_timeline_expand", { defaultValue: "Open timeline fullscreen" })}
          >
            {expanded ? "×" : "⛶"}
          </button>
        </div>
      </div>

      <div className="crm-journey-timeline__filters" aria-label={t("journey_filters_aria")}>
        {filters.map((filter) => {
          const isActive = activeFilter === filter.key;
          const hasEvents = filter.key === "all" || activeKinds.has(filter.key);
          return (
            <button
              key={filter.key}
              type="button"
              className={`crm-journey-timeline__filter ${isActive ? "is-active" : ""} ${hasEvents ? "has-events" : "is-muted"}`}
              onClick={() => {
                setActiveFilter(filter.key);
                setVisibleCount(6);
                setOpenEventKey(null);
              }}
            >
              <span className="crm-journey-timeline__filter-mark">{filter.icon}</span>
              <span>{t(filter.label)}</span>
            </button>
          );
        })}
      </div>

      <div className="crm-journey-timeline__body" aria-live="polite">
        {loading && <TimelineState>{t("loading", { defaultValue: "Loading" })}...</TimelineState>}
        {error && <TimelineState error>{error}</TimelineState>}
        {!loading && !error && filteredEvents.length === 0 && (
          <TimelineState>{t("crm_timeline_empty", { defaultValue: "No timeline events yet. New system events will appear here automatically." })}</TimelineState>
        )}

        {!loading && !error && shownEvents.map((event, index) => {
          const eventKey = String(
            event.id ||
              event.ui_id ||
              `${event.presentation?.bucket || "event"}-${index}`
          );

          return (
            <TimelineNode
              key={eventKey}
              event={event}
              eventKey={eventKey}
              t={t}
              isOpen={openEventKey === eventKey}
              onToggle={() =>
                setOpenEventKey((current) =>
                  current === eventKey ? null : eventKey
                )
              }
              onOpenPhoto={(files, fileIndex) =>
                setLightbox({ files, index: fileIndex })
              }
            />
          );
        })}

        {!loading && !error && hasMore && (
          <button type="button" className="crm-journey-timeline__more" onClick={() => setVisibleCount((count) => count + 6)}>
            {t("crm_timeline_load_more", { defaultValue: "Load more" })}
            <span aria-hidden="true">↓</span>
          </button>
        )}
      </div>

      {lightbox && (
        <PhotoLightbox
          files={lightbox.files}
          index={lightbox.index}
          onIndex={(nextIndex) => setLightbox((current) => current ? { ...current, index: nextIndex } : current)}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}

function TimelineState({ children, error = false }) {
  return <div className={`crm-journey-timeline__state ${error ? "is-error" : ""}`}>{children}</div>;
}


const parseArrowChange = (value) => {
  const text = safeText(value);
  const match = text.match(/^(.*?)\s*(?:→|->)\s*(.*?)$/);

  if (!match) return null;

  return {
    before: safeText(match[1]),
    after: safeText(match[2]),
  };
};

const parseNumericValue = (value) => {
  const normalized = safeText(value).replace(",", ".").replace(/[^\d.-]/g, "");
  if (!normalized) return null;

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const buildEventDetails = ({ event, presentation, detail, owner, t }) => {
  const title = safeText(presentation?.title).toLowerCase();
  const content = safeText(detail);
  const change = parseArrowChange(content);
  const rows = [];
  let description = "";
  let footer = "";

  const addRow = (label, value, emphasis = false) => {
    const safeValue = safeText(value);
    if (!safeValue) return;

    rows.push({
      label,
      value: safeValue,
      emphasis,
    });
  };

  if (
    title.includes("budget") ||
    title.includes("amount") ||
    title.includes("бюджет") ||
    title.includes("сумм")
  ) {
    if (change) {
      addRow(
        t("crm_timeline_before", { defaultValue: "Before" }),
        change.before
      );
      addRow(
        t("crm_timeline_after", { defaultValue: "After" }),
        change.after,
        true
      );

      const beforeNumber = parseNumericValue(change.before);
      const afterNumber = parseNumericValue(change.after);

      if (beforeNumber !== null && afterNumber !== null) {
        const delta = Number((afterNumber - beforeNumber).toFixed(2));
        const formattedDelta = `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`;

        addRow(
          t("crm_timeline_difference", { defaultValue: "Difference" }),
          formattedDelta
        );
      }
    } else {
      description = content;
    }
  } else if (
    title.includes("due") ||
    title.includes("moved") ||
    title.includes("deadline") ||
    title.includes("срок") ||
    title.includes("перенес")
  ) {
    const dueDateText = content.replace(/^.*?due date:\s*/i, "").trim();
    const dueChange = parseArrowChange(dueDateText);

    if (dueChange) {
      addRow(
        t("crm_timeline_previous_time", {
          defaultValue: "Previous time",
        }),
        dueChange.before
      );
      addRow(
        t("crm_timeline_new_time", {
          defaultValue: "New time",
        }),
        dueChange.after,
        true
      );
    } else {
      description = content;
    }

    const taskName = safeText(event.task_title || event.entity_name);
    if (taskName) {
      footer = `${t("crm_timeline_task", {
        defaultValue: "Task",
      })}: ${taskName}`;
    }
  } else if (
    title.includes("converted") ||
    title.includes("convert") ||
    title.includes("конверт")
  ) {
    description =
      content ||
      t("crm_timeline_conversion_completed", {
        defaultValue: "The lead was converted into a deal.",
      });

    if (event.lead_ui_id) {
      addRow("Lead", `#${event.lead_ui_id}`);
    }

    if (event.deal_ui_id) {
      addRow("Deal", `#${event.deal_ui_id}`, true);
    }
  } else if (
    title.includes("created") ||
    title.includes("создан")
  ) {
    description = content;

    const createdEntity =
      event.task_title ||
      event.lead_title ||
      event.deal_title ||
      event.contact_name ||
      event.entity_name;

    if (createdEntity) {
      addRow(
        t("crm_timeline_created_item", {
          defaultValue: "Created item",
        }),
        createdEntity,
        true
      );
    }
  } else if (
    title.includes("status") ||
    title.includes("stage") ||
    title.includes("статус") ||
    title.includes("этап")
  ) {
    if (change) {
      addRow(
        t("crm_timeline_before", { defaultValue: "Before" }),
        change.before
      );
      addRow(
        t("crm_timeline_after", { defaultValue: "After" }),
        change.after,
        true
      );
    } else {
      description = content;
    }
  } else {
    description = content;
  }

  if (owner) {
    addRow(
      t("crm_timeline_changed_by", {
        defaultValue: "Changed by",
      }),
      owner
    );
  }

  return {
    description,
    rows,
    footer,
  };
};

function TimelineNode({
  event,
  eventKey,
  t,
  isOpen,
  onToggle,
  onOpenPhoto,
}) {
  const presentation = event.presentation || detectEvent(event, t);
  const tone = eventColor[presentation.kind] || presentation.bucket || "system";
  const eventTime = getEventDate(event);
  const displayTime = formatDateTime(eventTime, t);
  const detail = compactDescription(event);
  const owner = safeText(
    event.owner_display_name ||
      event.owner_name ||
      event.owner ||
      event.created_by_name ||
      event.contact_name ||
      event.full_name
  );
  const attachments = getAttachments(event);
  const eventDetails = buildEventDetails({
    event,
    presentation,
    detail,
    owner,
    t,
  });


  return (
    <article
      className={`crm-journey-event is-${tone} ${isOpen ? "is-open" : ""}`}
      data-event-key={eventKey}
    >
      <div className="crm-journey-event__rail">
        <button
          type="button"
          className="crm-journey-event__dot"
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? t("crm_timeline_hide_details", {
                  defaultValue: "Hide event details",
                })
              : t("crm_timeline_show_details", {
                  defaultValue: "Show event details",
                })
          }
          onClick={onToggle}
        >
          <span aria-hidden="true">{presentation.icon}</span>
        </button>
      </div>

      <div className="crm-journey-event__content">
        <button
          type="button"
          className="crm-journey-event__summary"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <span className="crm-journey-event__main">
            <span className="crm-journey-event__title-row">
              <strong className="crm-journey-event__title">
                {presentation.title}
              </strong>
              <span className="crm-journey-event__tag">
                {presentation.label}
              </span>
            </span>

            {detail && (
              <span className="crm-journey-event__text">{detail}</span>
            )}
          </span>

          <span className="crm-journey-event__meta">
            {owner && <span>{owner}</span>}
            <time>{displayTime}</time>
            <span
              className="crm-journey-event__chevron"
              aria-hidden="true"
            >
              {isOpen ? "⌃" : "⌄"}
            </span>
          </span>
        </button>

        {attachments.length > 0 && !isOpen && (
          <PhotoStrip
            files={attachments}
            compact
            onOpen={(idx) => onOpenPhoto?.(attachments, idx)}
          />
        )}

        {isOpen && (
          <div className="crm-journey-event__details">
            {eventDetails.description && (
              <p className="crm-journey-event__details-description">
                {eventDetails.description}
              </p>
            )}

            {eventDetails.rows.length > 0 && (
              <dl className="crm-journey-event__change-list">
                {eventDetails.rows.map((field, index) => (
                  <div
                    key={`${field.label}-${field.value}-${index}`}
                    className={
                      field.emphasis
                        ? "crm-journey-event__change-row is-emphasis"
                        : "crm-journey-event__change-row"
                    }
                  >
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {eventDetails.footer && (
              <div className="crm-journey-event__details-footer-note">
                {eventDetails.footer}
              </div>
            )}

            {!eventDetails.description &&
              eventDetails.rows.length === 0 &&
              !eventDetails.footer && (
                <div className="crm-journey-event__details-empty">
                  {t("crm_timeline_no_extra_details", {
                    defaultValue: "No additional details were recorded.",
                  })}
                </div>
              )}

            <div className="crm-journey-event__details-time">
              <span>
                {t("crm_timeline_event_time", {
                  defaultValue: "Event time",
                })}
              </span>
              <time>{displayTime}</time>
            </div>

            {attachments.length > 0 && (
              <div className="crm-journey-event__details-photos">
                <div className="crm-journey-event__details-photos-title">
                  {attachments.length}{" "}
                  {attachments.length === 1 ? "photo" : "photos"}
                </div>
                <PhotoStrip
                  files={attachments}
                  onOpen={(idx) => onOpenPhoto?.(attachments, idx)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function filePreviewSrc(file) {
  if (file?.id) {
    const base = String(api?.defaults?.baseURL || "").replace(/\/$/, "");
    return `${base}/auth/storage/files/${file.id}/download`;
  }
  return file?.thumbnail_link || file?.web_content_link || file?.web_view_link || "";
}

function PhotoStrip({ files, compact = false, onOpen }) {
  const visible = (files || []).slice(0, compact ? 4 : 8);
  if (!visible.length) return null;

  return (
    <div className={`crm-journey-photos ${compact ? "is-compact" : ""}`}>
      {visible.map((file, idx) => (
        <button
          key={file.id || file.provider_file_id || idx}
          type="button"
          className="crm-journey-photo"
          title={file.file_name}
          onClick={(e) => { e.stopPropagation(); onOpen?.(idx); }}
        >
          {filePreviewSrc(file) ? (
            <img src={filePreviewSrc(file)} alt={file.file_name || "Photo"} loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <span>IMG</span>
          )}
          <small>{file.file_name}</small>
        </button>
      ))}
      {files.length > visible.length && (
        <span className="crm-journey-photo-more">+{files.length - visible.length}</span>
      )}
    </div>
  );
}

function PhotoLightbox({ files, index, onIndex, onClose }) {
  const { t } = useTranslation();
  const safeFiles = files || [];
  const currentIndex = Math.max(0, Math.min(index || 0, safeFiles.length - 1));
  const file = safeFiles[currentIndex];
  if (!file) return null;

  const go = (step) => {
    const next = (currentIndex + step + safeFiles.length) % safeFiles.length;
    onIndex(next);
  };

  return (
    <div className="crm-photo-lightbox" role="dialog" aria-modal="true">
      <div className="crm-photo-lightbox__bar">
        <div>
          <strong>{file.file_name || "Photo"}</strong>
          <span>{currentIndex + 1} / {safeFiles.length}</span>
        </div>
        <button type="button" onClick={onClose}>×</button>
      </div>

      <div className="crm-photo-lightbox__stage">
        {safeFiles.length > 1 && <button type="button" className="crm-photo-lightbox__nav is-left" onClick={() => go(-1)}>‹</button>}
        <img src={filePreviewSrc(file)} alt={file.file_name || "Photo"} referrerPolicy="no-referrer" />
        {safeFiles.length > 1 && <button type="button" className="crm-photo-lightbox__nav is-right" onClick={() => go(1)}>›</button>}
      </div>

      <div className="crm-photo-lightbox__footer">
        {file.web_view_link && (
          <a href={file.web_view_link} target="_blank" rel="noreferrer">{t("journey_open_google_drive")}</a>
        )}
      </div>
    </div>
  );
}
